import React, { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { dateLabel, kindLabel, sourceFileName, sourceLocation, sourceSnippet, sourcesHeader } from '../utils/sophieSources';

// The cited sources under one Ask Sophie answer: collapsed by default, one click
// (or Enter / Space) opens the list in place. Each entry: the [n] the answer text
// uses, the kind (report / session transcript / attachment) when known, file
// name + page, the upload date, and a short snippet of the excerpt.
export default function SophieSources({ sources }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(sources) || sources.length === 0) return null;
  const toggle = () => setOpen((v) => !v);

  return (
    <Box sx={{ mt: 0.5, mx: '10px' }} data-testid="sophie-sources">
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${open ? 'Hide' : 'Show'} the ${sources.length} cited sources for this answer`}
        data-track-id="consultation.transcript.toggle_sources"
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          color: '#29C0BB',
          '&:hover': { textDecoration: 'underline' },
          '&:focus-visible': { outline: '2px solid #29C0BB', outlineOffset: '2px', borderRadius: '4px' },
        }}
      >
        <DescriptionIcon sx={{ fontSize: '14px' }} />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {sourcesHeader(sources.length)}
        </Typography>
        {open ? <ExpandLessIcon sx={{ fontSize: '16px' }} /> : <ExpandMoreIcon sx={{ fontSize: '16px' }} />}
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.75, display: 'grid', gap: '6px' }}>
          {sources.map((source, i) => {
            const location = sourceLocation(source);
            const dated = dateLabel(source);
            const kind = kindLabel(source);
            return (
              <Box
                component="li"
                key={source.id || i}
                sx={{ p: '6px 8px', borderRadius: '6px', backgroundColor: '#F4FAFA', border: '1px solid #DCEFEE' }}
              >
                <Box sx={{ display: 'flex', gap: '6px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1C8C87', fontVariantNumeric: 'tabular-nums' }}>
                    [{source.n ?? i + 1}]
                  </Typography>
                  {kind && (
                    <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {kind}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1F2933', wordBreak: 'break-word' }}>
                    {sourceFileName(source)}
                    {location ? ` · ${location}` : ''}
                  </Typography>
                  {dated && (
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      {dated}
                    </Typography>
                  )}
                </Box>
                {source.excerpt && (
                  <Typography variant="caption" component="p" sx={{ m: 0, mt: '2px', color: '#4B5563', lineHeight: 1.4 }}>
                    {sourceSnippet(source)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}
